import { environment } from "@app/relay/relay-environment";
import AwsS3 from "@uppy/aws-s3";
import Uppy from "@uppy/core";
import type { UppyFile, ErrorResponse } from "@uppy/core";
import { DragDrop } from "@uppy/react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import type { uploadZone_PresignQuery } from "@app/queries/uploadZone_PresignQuery.graphql";
import type { uploadZone_AccountIngestStartMutation } from "@app/queries/uploadZone_AccountIngestStartMutation.graphql";
import type { uploadZone_PollQuery } from "@app/queries/uploadZone_PollQuery.graphql";

import "@uppy/core/dist/style.min.css";
import "@uppy/drag-drop/dist/style.min.css";

const presignQuery = graphql`
  query uploadZone_PresignQuery($fileExtension: String!, $nonce: String!) {
    uploadPresign(fileExtension: $fileExtension, nonce: $nonce) {
      signedUrl
      storageKey
    }
  }
`;

// A bulk drop maps every file straight into the ledger — the same promotion the
// per-account cards run (Statements::Ingest), with NO target so each file routes
// to its own filename-derived account (Chase / Wise / Deel in one batch). Runs
// async on a worker; we poll the multi-upload's phase for progress.
const ingestStartMutation = graphql`
  mutation uploadZone_AccountIngestStartMutation(
    $multiUploadInput: MultiUploadInputObject!
  ) {
    accountIngestStart(multiUploadInput: $multiUploadInput) {
      multiUpload {
        id
        phase
      }
      errors {
        message
      }
      warnings
    }
  }
`;

const pollQuery = graphql`
  query uploadZone_PollQuery($id: ID!) {
    multiUpload(id: $id) {
      phase
      state
      errorMessage
    }
  }
`;

// Each backend phase → a label + progress-bar fill, mirroring the per-account
// dropzone so both entry points read the same.
const PHASES: Record<string, { label: string; pct: number }> = {
  QUEUED: { label: "Queued", pct: 15 },
  PARSING: { label: "Parsing", pct: 40 },
  ATTRIBUTING: { label: "Applying rules", pct: 66 },
  CATEGORIZING: { label: "Categorizing", pct: 88 },
  DONE: { label: "Done", pct: 100 },
};

type UploadedDescriptor = {
  id: string;
  storage: "cache";
  metadata: { size: number; filename: string; mimeType: string };
};

export default function UploadZone({
  hint = "CSV exports from Chase, Wise (transaction history), or Deel (Detailed Payments + Payments Made)",
}: {
  hint?: string;
} = {}) {
  const navigate = useNavigate();
  const uploadedRef = useRef<UploadedDescriptor[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [phase, setPhase] = useState<string>("QUEUED");
  const [warnings, setWarnings] = useState<readonly string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stop polling if the component unmounts mid-import.
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  // Created inside an effect (not useState/useMemo) so it survives React
  // StrictMode's mount→unmount→remount: the cleanup's uppy.close() uninstalls the
  // AwsS3 uploader on the simulated unmount, and a one-time factory never re-adds
  // it — leaving uploads with no uploader, silently "succeeding" without reaching
  // S3. Owning creation here rebuilds the instance (and uploader) on remount.
  const [uppy, setUppy] = useState<Uppy | null>(null);

  useEffect(() => {
    const instance = new Uppy({
      restrictions: { allowedFileTypes: [".csv"], maxNumberOfFiles: 50 },
      autoProceed: true,
    }).use(AwsS3, {
      async getUploadParameters(file: UppyFile) {
        const extension = file.name.split(".").pop() ?? "csv";
        // file.id is unique per file — it makes each presign request distinct so
        // Relay doesn't dedupe concurrent same-extension requests into one shared
        // presign (which would upload every file to the same S3 key).
        const data = await fetchQuery<uploadZone_PresignQuery>(
          environment,
          presignQuery,
          { fileExtension: extension, nonce: file.id },
          { fetchPolicy: "network-only" }
        ).toPromise();

        const presign = data?.uploadPresign;
        if (!presign) throw new Error("Could not presign upload");

        instance.setFileMeta(file.id, { shrineId: presign.storageKey });

        return {
          method: "PUT",
          url: presign.signedUrl,
          fields: {},
          headers: { "Content-Type": file.type ?? "text/csv" },
        };
      },
    });
    setUppy(instance);
    return () => instance.close();
  }, []);

  useEffect(() => {
    if (!uppy) return;
    const onSuccess = (file: UppyFile | undefined) => {
      if (!file) return;

      uploadedRef.current.push({
        id: String(file.meta.shrineId),
        storage: "cache",
        metadata: {
          size: file.size,
          filename: file.name,
          mimeType: file.type ?? "text/csv",
        },
      });
      setUploadedFiles((prev) => [...prev, file.name]);
    };

    // Surface upload failures on the page. Without these listeners a failed PUT
    // (bad/missing S3 config, CORS, a 404 from the FileSystem-fallback endpoint,
    // a rejected presign) only logs "Upload error" to the console and the UI
    // sits silent. Show the file, the HTTP status, and the response body/message
    // so the actual cause is visible.
    const onUploadError = (
      file: UppyFile | undefined,
      err: Error,
      response?: ErrorResponse,
    ) => {
      const body = response?.body;
      const detail =
        typeof body === "string" && body.length > 0
          ? body.slice(0, 300)
          : body != null
            ? JSON.stringify(body).slice(0, 300)
            : err.message;
      const label = file?.name ? `“${file.name}”` : "File";
      const status = response?.status ? ` (HTTP ${response.status})` : "";
      setError(`Upload failed for ${label}${status}: ${detail}`);
    };

    // Wrong file type / too many files, etc. — Uppy blocks these before upload.
    const onRestrictionFailed = (file: UppyFile | undefined, err: Error) => {
      setError(`${file?.name ? `“${file.name}”: ` : ""}${err.message}`);
    };

    // A fresh drop is a new attempt — clear any stale error.
    const onFileAdded = () => setError(null);

    uppy.on("upload-success", onSuccess);
    uppy.on("upload-error", onUploadError);
    uppy.on("restriction-failed", onRestrictionFailed);
    uppy.on("file-added", onFileAdded);
    return () => {
      uppy.off("upload-success", onSuccess);
      uppy.off("upload-error", onUploadError);
      uppy.off("restriction-failed", onRestrictionFailed);
      uppy.off("file-added", onFileAdded);
    };
  }, [uppy]);

  const startPolling = (multiUploadId: string) => {
    pollRef.current = setInterval(() => {
      fetchQuery<uploadZone_PollQuery>(environment, pollQuery, {
        id: multiUploadId,
      })
        .toPromise()
        .then((data) => {
          const mu = data?.multiUpload;
          if (!mu) return;
          if (mu.phase) setPhase(mu.phase);
          if (mu.phase === "DONE") {
            if (pollRef.current) clearInterval(pollRef.current);
            navigate({
              to: "/statements/imports/$multiUploadId",
              params: { multiUploadId },
            });
          } else if (mu.phase === "FAILED" || mu.state === "FAILED") {
            if (pollRef.current) clearInterval(pollRef.current);
            setImporting(false);
            setError(mu.errorMessage ?? "Import failed");
          }
        });
    }, 1000);
  };

  const startImport = () => {
    setImporting(true);
    setPhase("QUEUED");
    setError(null);

    commitMutation<uploadZone_AccountIngestStartMutation>(environment, {
      mutation: ingestStartMutation,
      variables: { multiUploadInput: { uploads: uploadedRef.current } },
      onCompleted: (response) => {
        const result = response.accountIngestStart;
        setWarnings(result?.warnings ?? []);
        const multiUploadId = result?.multiUpload?.id;
        if (multiUploadId) {
          startPolling(multiUploadId);
        } else {
          setImporting(false);
          setError(result?.errors?.[0]?.message ?? "Import failed");
        }
      },
      onError: (err) => {
        setImporting(false);
        setError(err.message);
      },
    });
  };

  return (
    <div className="bg-white rounded border p-4 space-y-3 [&_.uppy-DragDrop-container]:min-h-24">
      <div className="flex items-baseline justify-between">
        <h2 className="font-bold">Upload statements</h2>
        <p className="text-xs text-gray-400">{hint}</p>
      </div>

      {uppy && <DragDrop uppy={uppy} height="96px" />}

      {uploadedFiles.length > 0 && (
        <ul className="text-sm divide-y rounded border">
          {uploadedFiles.map((name) => (
            <li key={name} className="px-3 py-1.5 flex justify-between">
              <span>{name}</span>
              <span className="text-green-600">uploaded</span>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 break-words"
        >
          {error}
        </div>
      )}

      {importing ? (
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
            <span>{(PHASES[phase] ?? PHASES.QUEUED).label}…</span>
            <span>{(PHASES[phase] ?? PHASES.QUEUED).pct}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded bg-gray-100">
            <div
              className="h-full bg-gray-900 transition-all duration-500 ease-out"
              style={{ width: `${(PHASES[phase] ?? PHASES.QUEUED).pct}%` }}
            />
          </div>
          {warnings.map((warning) => (
            <p key={warning} className="mt-1 text-xs text-amber-700">
              {warning}
            </p>
          ))}
        </div>
      ) : (
        uploadedFiles.length > 0 && (
          <button
            type="button"
            onClick={startImport}
            className="rounded bg-gray-900 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {`Import ${uploadedFiles.length} file(s) into the ledger`}
          </button>
        )
      )}
    </div>
  );
}
